import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { MapPin, ArrowSquareOut, X as XIcon, MagnifyingGlass, PencilSimple } from "@phosphor-icons/react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { geocodeAddress, suggestAddress, updateTruck } from "@/lib/api";

// Fix default marker icons for Leaflet in bundlers (CRA)
const truckIcon = L.divIcon({
  html: `<div style="background:#111827;color:#fbbf24;border:2px solid #fbbf24;border-radius:50%;width:34px;height:34px;display:flex;align-items:center;justify-content:center;font-weight:900;font-size:16px;box-shadow:0 4px 12px rgba(0,0,0,0.35);">📍</div>`,
  className: "",
  iconSize: [34, 34],
  iconAnchor: [17, 17],
});

function RecenterOnCoords({ lat, lng }) {
  const map = useMap();
  useEffect(() => {
    if (typeof lat === "number" && typeof lng === "number") {
      map.setView([lat, lng], Math.max(map.getZoom(), 14), { animate: true });
    }
  }, [lat, lng, map]);
  return null;
}

function formatUpdatedAt(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString("fr-FR", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso.slice(0, 16).replace("T", " ");
  }
}

export default function TruckLocationCard({ truck, onUpdated }) {
  const [editing, setEditing] = useState(false);
  const [input, setInput] = useState(truck?.location_address || "");
  const [note, setNote] = useState(truck?.location_note || "");
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null); // { lat, lng, display_name } pending save
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    setInput(truck?.location_address || "");
    setNote(truck?.location_note || "");
    setPreview(null);
    setSuggestions([]);
  }, [truck?.id]);

  // Autocomplete: debounced fetch of suggestions as user types (min 3 chars)
  useEffect(() => {
    if (!editing) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = input.trim();
    if (q.length < 3) {
      setSuggestions([]);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await suggestAddress(q);
        setSuggestions(res.results || []);
        setShowSuggestions(true);
      } catch {
        setSuggestions([]);
      }
    }, 300);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [input, editing]);

  const hasLocation =
    typeof truck?.location_lat === "number" && typeof truck?.location_lng === "number";
  const isBroken = truck?.status === "panne";
  const showCard = hasLocation || editing;
  const showBrokenBanner = isBroken && !hasLocation && !editing;

  const activeLat = preview?.lat ?? truck?.location_lat;
  const activeLng = preview?.lng ?? truck?.location_lng;
  const activeName = preview?.display_name ?? truck?.location_address ?? "";

  const gmapsUrl = useMemo(() => {
    if (typeof activeLat !== "number" || typeof activeLng !== "number") return "";
    return `https://www.google.com/maps/dir/?api=1&destination=${activeLat},${activeLng}`;
  }, [activeLat, activeLng]);

  const wazeUrl = useMemo(() => {
    if (typeof activeLat !== "number" || typeof activeLng !== "number") return "";
    return `https://waze.com/ul?ll=${activeLat},${activeLng}&navigate=yes`;
  }, [activeLat, activeLng]);

  const pickSuggestion = (s) => {
    setInput(s.display_name);
    setPreview({ lat: s.lat, lng: s.lng, display_name: s.display_name });
    setShowSuggestions(false);
    setSuggestions([]);
    toast.success("Position sélectionnée — clique Enregistrer");
  };

  const runGeocode = async () => {
    const q = input.trim();
    if (!q) {
      toast.error("Saisis une adresse ou colle des coordonnées");
      return;
    }
    setLoading(true);
    try {
      const res = await geocodeAddress(q);
      setPreview(res);
      toast.success("Position trouvée — vérifie puis Enregistre");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Adresse introuvable");
    } finally {
      setLoading(false);
    }
  };

  const saveLocation = async () => {
    if (!preview) {
      toast.error("Cherche d'abord la position");
      return;
    }
    try {
      setLoading(true);
      const updated = await updateTruck(truck.id, {
        location_address: preview.display_name,
        location_lat: preview.lat,
        location_lng: preview.lng,
        location_note: note,
      });
      toast.success("Position enregistrée");
      setEditing(false);
      setPreview(null);
      onUpdated?.(updated);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur enregistrement");
    } finally {
      setLoading(false);
    }
  };

  const clearLocation = async () => {
    try {
      setLoading(true);
      const updated = await updateTruck(truck.id, {
        location_address: "",
        location_lat: null,
        location_lng: null,
        location_note: "",
      });
      toast.success("Position effacée");
      setEditing(false);
      setPreview(null);
      setInput("");
      setNote("");
      onUpdated?.(updated);
    } catch {
      toast.error("Erreur");
    } finally {
      setLoading(false);
    }
  };

  if (showBrokenBanner) {
    return (
      <div
        className="border-2 border-red-700 bg-red-50 mb-4 flex items-center justify-between px-4 py-3"
        data-testid="location-card-broken-banner"
      >
        <div className="flex items-center gap-3 text-red-800">
          <MapPin size={20} weight="fill" />
          <div>
            <div className="text-[11px] uppercase tracking-widest font-black">
              🚨 Camion en panne — position non renseignée
            </div>
            <div className="text-[10px] text-red-600 mt-0.5">
              Renseigne la position pour lancer la dépanneuse
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setEditing(true)}
          className="px-3 py-2 bg-red-700 text-white text-[11px] uppercase tracking-widest font-bold hover:bg-red-800"
          data-testid="location-add-broken-btn"
        >
          + Renseigner
        </button>
      </div>
    );
  }

  if (!showCard) {
    // Compact CTA to add a location, hidden when no interest
    return (
      <div
        className="border-2 border-dashed border-zinc-300 bg-zinc-50 p-4 mb-4 flex items-center justify-between"
        data-testid="location-card-empty"
      >
        <div className="flex items-center gap-3 text-sm text-zinc-500">
          <MapPin size={20} weight="duotone" />
          <span className="uppercase tracking-widest text-[11px] font-bold">
            Aucune position enregistrée
          </span>
        </div>
        <button
          type="button"
          onClick={() => setEditing(true)}
          className="text-[11px] uppercase tracking-widest font-bold px-3 py-1.5 border border-zinc-900 hover:bg-zinc-900 hover:text-white"
          data-testid="location-add-btn"
        >
          + Ajouter position
        </button>
      </div>
    );
  }

  return (
    <div
      className={`border-2 mb-4 ${isBroken ? "border-red-700 bg-red-50" : "border-zinc-900 bg-white"}`}
      data-testid="location-card"
    >
      {/* Header */}
      <div className={`px-4 py-2 flex items-center justify-between ${isBroken ? "bg-red-700 text-white" : "bg-zinc-900 text-[var(--ny-gold)]"}`}>
        <div className="flex items-center gap-2">
          <MapPin size={16} weight="fill" />
          <span className="text-[11px] uppercase tracking-widest font-black">
            {isBroken ? "🚨 Camion en panne · Position" : "Position du camion"}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {truck?.location_updated_at && !editing && (
            <span className="text-[10px] uppercase tracking-widest opacity-80 hidden md:inline">
              MAJ {formatUpdatedAt(truck.location_updated_at)}
            </span>
          )}
          {!editing && (
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="text-[10px] uppercase tracking-widest font-bold px-2 py-1 border border-current hover:bg-white/10"
              data-testid="location-edit-btn"
            >
              <PencilSimple size={11} weight="bold" className="inline mr-1" />
              Modifier
            </button>
          )}
        </div>
      </div>

      {/* Editing form */}
      {editing && (
        <div className="p-4 space-y-3 border-b border-zinc-200">
          <div>
            <label className="text-[10px] uppercase tracking-widest font-bold text-zinc-500 block mb-1">
              Adresse, coordonnées GPS ou lien Google Maps
            </label>
            <div className="relative">
              <div className="flex gap-2">
                <input
                  ref={inputRef}
                  type="text"
                  value={input}
                  onChange={(e) => {
                    setInput(e.target.value);
                    setPreview(null);
                  }}
                  onFocus={() => setShowSuggestions(true)}
                  onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                  placeholder="Commence à taper (ex: gare perpignan, 12 rue rivoli paris...)"
                  className="flex-1 border border-zinc-900 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[var(--ny-gold)]"
                  data-testid="location-input"
                  autoComplete="off"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      if (suggestions.length > 0) {
                        pickSuggestion(suggestions[0]);
                      } else {
                        runGeocode();
                      }
                    }
                    if (e.key === "Escape") setShowSuggestions(false);
                  }}
                />
                <button
                  type="button"
                  onClick={runGeocode}
                  disabled={loading}
                  className="px-4 py-2 bg-zinc-900 text-white text-[11px] uppercase tracking-widest font-bold hover:bg-zinc-700 disabled:opacity-50 inline-flex items-center gap-2"
                  data-testid="location-search-btn"
                >
                  <MagnifyingGlass size={14} weight="bold" />
                  {loading ? "…" : "Ok"}
                </button>
              </div>
              {/* Autocomplete dropdown */}
              {showSuggestions && suggestions.length > 0 && (
                <div
                  className="absolute z-30 left-0 right-0 top-full mt-1 bg-white border-2 border-zinc-900 shadow-lg max-h-72 overflow-y-auto"
                  data-testid="location-suggestions"
                >
                  {suggestions.map((s, i) => (
                    <button
                      key={`${s.lat}-${s.lng}-${i}`}
                      type="button"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        pickSuggestion(s);
                      }}
                      className="w-full text-left px-3 py-2 hover:bg-[var(--ny-gold)]/20 border-b border-zinc-100 last:border-b-0 flex items-start gap-2"
                      data-testid={`location-suggestion-${i}`}
                    >
                      <MapPin size={14} weight="duotone" className="text-[var(--ny-gold)] mt-0.5 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-zinc-800 truncate">{s.display_name}</div>
                        <div className="text-[10px] mono text-zinc-400 mt-0.5">
                          {s.lat.toFixed(5)}, {s.lng.toFixed(5)}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <p className="text-[10px] text-zinc-400 mt-1">
              💡 Tape ≥ 3 lettres et clique une suggestion, ou colle un lien Google Maps / des coordonnées GPS puis clique Ok.
            </p>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-widest font-bold text-zinc-500 block mb-1">
              Note (optionnelle)
            </label>
            <input
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Ex: Sur la BAU, à côté du panneau km 42"
              className="w-full border border-zinc-900 px-3 py-2 text-sm"
              data-testid="location-note-input"
            />
          </div>

          {preview && (
            <div className="bg-[var(--ny-gold)]/15 border border-[var(--ny-gold)] p-2 text-xs">
              <div className="font-bold text-zinc-800 mb-1">Position trouvée</div>
              <div className="text-zinc-600">{preview.display_name}</div>
              <div className="mono text-[10px] text-zinc-500 mt-1">
                {preview.lat.toFixed(6)}, {preview.lng.toFixed(6)}
              </div>
            </div>
          )}

          <div className="flex gap-2 justify-end">
            {hasLocation && (
              <button
                type="button"
                onClick={clearLocation}
                disabled={loading}
                className="px-3 py-2 text-[11px] uppercase tracking-widest font-bold border border-red-700 text-red-700 hover:bg-red-700 hover:text-white inline-flex items-center gap-1"
                data-testid="location-clear-btn"
              >
                <XIcon size={12} weight="bold" />
                Effacer
              </button>
            )}
            <button
              type="button"
              onClick={() => {
                setEditing(false);
                setPreview(null);
                setInput(truck?.location_address || "");
                setNote(truck?.location_note || "");
              }}
              className="px-3 py-2 text-[11px] uppercase tracking-widest font-bold border border-zinc-400 hover:bg-zinc-100"
              data-testid="location-cancel-btn"
            >
              Annuler
            </button>
            <button
              type="button"
              onClick={saveLocation}
              disabled={loading || !preview}
              className="px-4 py-2 bg-[var(--ny-gold)] text-zinc-900 text-[11px] uppercase tracking-widest font-black hover:bg-yellow-400 disabled:opacity-50"
              data-testid="location-save-btn"
            >
              {loading ? "…" : "Enregistrer"}
            </button>
          </div>
        </div>
      )}

      {/* Map + address display */}
      {typeof activeLat === "number" && typeof activeLng === "number" && (
        <div>
          <div className="h-64 relative" data-testid="location-map">
            <MapContainer
              center={[activeLat, activeLng]}
              zoom={14}
              style={{ height: "100%", width: "100%" }}
              scrollWheelZoom={false}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <Marker position={[activeLat, activeLng]} icon={truckIcon}>
                <Popup>
                  <div className="text-xs">
                    <div className="font-bold">{truck.license_plate}</div>
                    <div className="mt-1">{activeName}</div>
                  </div>
                </Popup>
              </Marker>
              <RecenterOnCoords lat={activeLat} lng={activeLng} />
            </MapContainer>
          </div>
          <div className="p-3 flex flex-wrap items-center justify-between gap-3 border-t border-zinc-200 bg-zinc-50">
            <div className="text-xs text-zinc-700 flex-1 min-w-[200px]">
              <div className="font-bold">{activeName || "Position enregistrée"}</div>
              {!preview && truck?.location_note && (
                <div className="text-zinc-500 italic mt-1">📝 {truck.location_note}</div>
              )}
              <div className="mono text-[10px] text-zinc-400 mt-1">
                {activeLat.toFixed(6)}, {activeLng.toFixed(6)}
              </div>
            </div>
            <div className="flex gap-2 flex-wrap">
              <a
                href={gmapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="px-3 py-2 bg-blue-600 text-white text-[11px] uppercase tracking-widest font-bold hover:bg-blue-700 inline-flex items-center gap-1"
                data-testid="location-gmaps-btn"
              >
                <ArrowSquareOut size={12} weight="bold" />
                Google Maps
              </a>
              <a
                href={wazeUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="px-3 py-2 bg-cyan-500 text-white text-[11px] uppercase tracking-widest font-bold hover:bg-cyan-600 inline-flex items-center gap-1"
                data-testid="location-waze-btn"
              >
                <ArrowSquareOut size={12} weight="bold" />
                Waze
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
